import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { INPUT, BTN_PRIMARY, BTN_OUTLINE, Toggle, Field } from "components/admin/AdminUI";
import api from "lib/api";

export default function UserForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [bucket, setBucket] = useState("");
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null); // credenciais quando sem SMTP

  useEffect(() => {
    api.get("/admin/buckets/")
      .then(({ data }) => setBuckets(data.results || data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    api.get(`/admin/users/${id}/`)
      .then(({ data }) => {
        setEmail(data.email || "");
        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        setCompanyName(data.company_name || "");
        setIsAdmin(data.is_admin || false);
        setIsActive(data.is_active ?? true);
        setBucket(data.bucket || "");
      })
      .catch(() => setError("Erro ao carregar usuário."))
      .finally(() => setFetching(false));
  }, [id, isEditing]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = {
        email, first_name: firstName, last_name: lastName,
        company_name: companyName, is_admin: isAdmin, is_active: isActive,
        bucket: bucket || null,
      };
      if (password) payload.password = password;
      if (isEditing) {
        await api.put(`/admin/users/${id}/`, payload);
        navigate("/admin/users");
      } else {
        const { data } = await api.post("/admin/users/", payload);
        // sem SMTP, o backend devolve as credenciais p/ repasse manual.
        if (data.initial_password || data.otpauth_uri) setCreated(data);
        else navigate("/admin/users");
      }
    } catch (err) {
      const detail = err.response?.data;
      setError(typeof detail === "object" ? Object.values(detail).flat().join(" ") : "Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  }

  if (fetching) {
    return <div className="flex justify-center py-12"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" /></div>;
  }

  if (created) {
    return (
      <div>
        <h1 className="mb-1 text-2xl font-bold tracking-tight">Usuário criado</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          SMTP não configurado — repasse estas credenciais ao usuário (não serão exibidas de novo).
        </p>
        <div className="w-full space-y-3 rounded-lg border border-border bg-card p-6 text-sm">
          <div><span className="text-muted-foreground">E-mail:</span> <span className="font-mono">{created.email}</span></div>
          {created.initial_password && (
            <div><span className="text-muted-foreground">Senha inicial:</span> <span className="font-mono">{created.initial_password}</span></div>
          )}
          {created.otpauth_uri && (
            <div className="break-all"><span className="text-muted-foreground">MFA (otpauth):</span> <span className="font-mono text-xs">{created.otpauth_uri}</span></div>
          )}
          <p className="text-xs text-muted-foreground">O usuário deverá trocar a senha no primeiro acesso e cadastrar o MFA no app autenticador.</p>
        </div>
        <div className="mt-4">
          <button className={BTN_PRIMARY} onClick={() => navigate("/admin/users")}>Concluir</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate("/admin/users")}
        className="mb-4 flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar para Usuários
      </button>

      <h1 className="mb-1 text-2xl font-bold tracking-tight">{isEditing ? "Editar Usuário" : "Novo Usuário"}</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        {isEditing ? "Altere os dados do usuário." : "Senha inicial e QR do MFA são enviados por e-mail (ou exibidos aqui se não houver SMTP)."}
      </p>

      <div className="w-full rounded-lg border border-border bg-card p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Email">
            <input className={INPUT} type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@exemplo.com" required disabled={isEditing} autoFocus={!isEditing} />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nome"><input className={INPUT} value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Field>
            <Field label="Sobrenome"><input className={INPUT} value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
          </div>
          <Field label="Empresa"><input className={INPUT} value={companyName} onChange={(e) => setCompanyName(e.target.value)} /></Field>
          <Field label="Bucket (rate-limit)">
            <select className={INPUT} value={bucket} onChange={(e) => setBucket(e.target.value)}>
              <option value="">Padrão (1000/dia)</option>
              {buckets.map((b) => (
                <option key={b.id} value={b.id}>{b.name} ({b.daily_limit}/dia)</option>
              ))}
            </select>
          </Field>
          <Field label="Senha">
            <input className={INPUT} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={isEditing ? "deixe em branco para manter" : "deixe em branco para gerar e enviar por e-mail"} />
          </Field>
          <Toggle checked={isAdmin} onChange={setIsAdmin} label="Administrador" description="Acesso à área administrativa." />
          <Toggle checked={isActive} onChange={setIsActive} label="Ativo" description="Permite o acesso do usuário." />

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className={BTN_OUTLINE} onClick={() => navigate("/admin/users")}>Cancelar</button>
            <button type="submit" className={BTN_PRIMARY} disabled={loading}>{loading ? "..." : isEditing ? "Salvar" : "Criar"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
